import { FC, useCallback, useState } from "react";
import { AnalyticsEvent } from "@common/constants/analytics-events";
import { FLAT_RATE_PLANS, PREMIUM_FEATURES } from "@common/flatRateBilling";
import { PLAN_CAPABILITY_UPGRADE_MESSAGES } from "@common/planCapabilities";
import { UploadIcon } from "@icons";
import { ConnectPlaid } from "@components/plaid/ConnectPlaid/ConnectPlaid";
import { StandardModal } from "@design-system/Modal/Modal";
import { useFetchAsset } from "@hooks/useFetchAsset";
import { InstitutionIcon } from "@design-system/InstitutionIcon/InstitutionIcon";
import { AccountsIntegrationCommandItems } from "./AccountsIntegrationCommandItems";
import { RELAY_PREVIEW_URL } from "@constants";
import { Command } from "@vendor/cmdk/cmdk";
import { GustoConnectionModal } from "@components/gusto/GustoConnectionModal";
import { useSettings } from "@components/settings/SettingsProvider";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { useCanConnectIntegrations } from "@hooks/useCanConnectIntegrations";
import { FeatureUpgradeModal } from "@components/billing/FeatureUpgradeModal";
import { trackEvent } from "@lib/analytics";
import { useOverlays } from "contexts/OverlayContext";
import { OrgConnectProvider } from "contexts/OrgConnectContext";
import { CommandItem } from "@design-system/CommandItem/CommandItem";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import styles from "./Commands.module.scss";
import { CommandsListProps } from "./lib";
import { useCommands } from "./useCommands";
import { RelayModal } from "./RelayModal";

const PLAID_SUPPORTED_ACCOUNTS = [
    "us bank",
    "pnc",
    "usaa",
    "td",
    "regions",
    "navy federal credit union",
    "charles schwab",
    "citizens",
    "huntigton",
    "betterment",
    "merill lynch",
    "vanguard",
];

interface AccountsCommandsListProps extends CommandsListProps {
    includeStaticAccounts?: boolean;
}

export const AccountsCommandsList: FC<AccountsCommandsListProps> = ({
    includeStaticAccounts = false,
    options = {},
}) => {
    const { organizationId } = options;

    if (organizationId) {
        return (
            <OrgConnectProvider organizationId={organizationId}>
                <AccountsCommandsListContent
                    includeStaticAccounts={includeStaticAccounts}
                    options={options}
                />
            </OrgConnectProvider>
        );
    }

    return (
        <AccountsCommandsListContent
            includeStaticAccounts={includeStaticAccounts}
            options={options}
        />
    );
};

const AccountsCommandsListContent: FC<AccountsCommandsListProps> = ({
    includeStaticAccounts = false,
    options = {},
}) => {
    const isOrgConnect = Boolean(options.organizationId);
    const { close: closeCommands } = useCommands();
    const { isOpened: isSettingsOpen, closeWithOverlay } = useSettings();
    const { openOverlay } = useOverlays();
    const [showRelay, setShowRelay] = useState(false);
    const [showImporterUpgradeModal, setShowImporterUpgradeModal] =
        useState(false);
    const { hasAccess: hasImporterAccess, isFetched: isImporterAccessFetched } =
        useAllowFeature(PREMIUM_FEATURES.IMPORTER);
    const canConnectIntegrations = useCanConnectIntegrations();
    const [showIntegrationsUpgradeModal, setShowIntegrationsUpgradeModal] =
        useState(false);
    useFetchAsset(RELAY_PREVIEW_URL);

    const [showGustoConnectionModal, setShowGustoConnectionModal] =
        useState(false);
    const onHideGustoConnectionModal = useCallback(() => {
        setShowGustoConnectionModal(false);
    }, [setShowGustoConnectionModal]);
    const initializeGustoConnection = useCallback(() => {
        setShowGustoConnectionModal(true);
    }, [setShowGustoConnectionModal]);

    const goToImporter = useCallback(() => {
        closeCommands();
        if (isSettingsOpen) {
            closeWithOverlay("importer", true);
        } else {
            openOverlay("importer", true);
        }
    }, [closeCommands, closeWithOverlay, isSettingsOpen, openOverlay]);

    const { refuseIfViewOnly } = useViewOnlyRefusal();

    const handleImportTransactions = useCallback(() => {
        // Refused at the click rather than at the upload: the importer would
        // otherwise open, take a file and parse it before the server said no.
        if (refuseIfViewOnly()) {
            return;
        }

        if (hasImporterAccess) {
            goToImporter();
            return;
        }

        void trackEvent(AnalyticsEvent.UPSELL_IMPORTER_ATTEMPTED);
        setShowImporterUpgradeModal(true);
    }, [goToImporter, hasImporterAccess, refuseIfViewOnly]);

    return (
        <>
            <Command.Group heading="Accounts">
                {canConnectIntegrations ? (
                    <AccountsIntegrationCommandItems
                        defaultEntity={options.defaultEntity}
                        onConnected={closeCommands}
                        onSelectGusto={initializeGustoConnection}
                        onSelectRelay={() => setShowRelay(true)}
                    />
                ) : (
                    <CommandItem
                        onSelect={() => setShowIntegrationsUpgradeModal(true)}
                        keywords={["integrations", "connect", "bank"]}
                    >
                        {
                            PLAN_CAPABILITY_UPGRADE_MESSAGES[
                                PREMIUM_FEATURES.INTEGRATIONS
                            ]
                        }
                    </CommandItem>
                )}

                {includeStaticAccounts && (
                    <>
                        {canConnectIntegrations && (
                            <ConnectPlaid
                                onConnected={closeCommands}
                                defaultEntity={options.defaultEntity}
                            >
                                {({ connecting, initializeConnection }) => (
                                    <CommandItem
                                        onSelect={initializeConnection}
                                        disabled={connecting}
                                        keywords={PLAID_SUPPORTED_ACCOUNTS}
                                    >
                                        <InstitutionIcon
                                            institution="plaid-white"
                                            size="sm"
                                        />{" "}
                                        Add other bank institutions with Plaid
                                    </CommandItem>
                                )}
                            </ConnectPlaid>
                        )}

                        {/* Imported transactions are not a connection, so they
                        cannot be listed in the org integrations table. */}
                        {!isOrgConnect && (
                            <CommandItem
                                onSelect={handleImportTransactions}
                                keywords={["pdf", "images", "csv"]}
                                disabled={!isImporterAccessFetched}
                            >
                                <span className={styles.customIcon}>
                                    <UploadIcon />
                                </span>
                                <span>
                                    Import Transactions{" "}
                                    <span className={styles.info}>
                                        (.csv, .pdf)
                                    </span>
                                </span>
                            </CommandItem>
                        )}
                    </>
                )}
            </Command.Group>

            <StandardModal show={showRelay} onHide={() => setShowRelay(false)}>
                <RelayModal defaultEntity={options.defaultEntity} />
            </StandardModal>

            <GustoConnectionModal
                show={showGustoConnectionModal}
                onHide={onHideGustoConnectionModal}
            />
            <FeatureUpgradeModal
                show={showIntegrationsUpgradeModal}
                onHide={() => setShowIntegrationsUpgradeModal(false)}
                plan={FLAT_RATE_PLANS.BASIC}
                text={
                    <>
                        Integrations are available on{" "}
                        <span className="text-blue-600">Basic</span>
                    </>
                }
                description={
                    <>
                        Connect banks, payment processors, and payroll to import
                        transactions automatically.
                    </>
                }
            />
            <FeatureUpgradeModal
                show={showImporterUpgradeModal}
                onHide={() => setShowImporterUpgradeModal(false)}
                plan={FLAT_RATE_PLANS.BASIC}
                text={
                    <>
                        Importer is available on{" "}
                        <span className="text-blue-600">Basic</span>
                    </>
                }
                description={
                    <>
                        Upload and automatically transform CSVs and PDFs from
                        any institution into transactions in minutes.
                    </>
                }
            />
        </>
    );
};
